import { AssessmentUuids, LiveSession, LiveSessionUpdate, VerityApi } from "../api/fetch";
import { ApiClientResponseError } from "@verity/api-client";
import { changeTimeOnly } from "../core/datetime-picker/TimePicker";

export type ProctorInfo = { id: string; name?: string };

export type ProctorGroupInfo = {
	id: number;
	name: string;
	minHoursNotice: number;
	trackCancellationHoursBefore: number;
	trackCancellationMessage?: string;
	autoCancelEmptyHoursBefore?: number;
};

export type LiveSessionWithInvitations = LiveSession & {
	invitations: { liveSessionId: string; proctorGroupId: number }[];
};

export type PendingLiveSession = Pick<
	LiveSessionUpdate,
	"name" | "startTime" | "endTime" | "capacity" | "proctorGuide"
> & {
	identifier: string;
	id?: string;
	proctorId?: string;
	proctorGroupId?: number;
	registrationStartTime?: Date;
	registrationEndTime?: Date;
};

export type SessionIssue =
	| "noName"
	| "noSessions"
	| "timesInvalid"
	| "startTimePassed"
	| "endTimePassed"
	| "durationTooLong"
	| "capacityInvalid"
	| "noProctor"
	| "proctorGroupNotice"
	| "registrationTimesInvalid"
	| "registrationStartTimePassed"
	| "registrationEndTimePassed"
	| "registrationStartTimeAfterStartTime"
	| "registrationEndTimeAfterEndTime"
	| "registrationEndTimeAfterCancellationTime"
	| "unfilledRegistrationTimeField";

function convertLiveSessionToPendingSession({
	id,
	name,
	startTime,
	endTime,
	capacity,
	registrationStartTime,
	registrationEndTime,
	users,
	invitations,
	proctorGuide,
}: LiveSessionWithInvitations): PendingLiveSession {
	return {
		identifier: id,
		id,
		name,
		startTime: new Date(startTime),
		endTime: new Date(endTime),
		proctorGuide: proctorGuide ?? undefined,
		capacity,
		proctorId: users.find(({ role }) => role === "proctor")?.userId,
		proctorGroupId: invitations[0]?.proctorGroupId,
		registrationStartTime: registrationStartTime == null ? undefined : new Date(registrationStartTime),
		registrationEndTime: registrationEndTime == null ? undefined : new Date(registrationEndTime),
	};
}

export function convertLiveSessionsToPendingSessions(
	sessions: LiveSessionWithInvitations[],
): Record<string, PendingLiveSession> {
	return Object.fromEntries(sessions.map((session) => [session.id, convertLiveSessionToPendingSession(session)]));
}

export function isLiveSessionNameChanged(existingSession: LiveSession, { name }: PendingLiveSession) {
	return existingSession.name !== name;
}

export function areLiveSessionTimesChanged(existingSession: LiveSession, { startTime, endTime }: PendingLiveSession) {
	return (
		new Date(existingSession.startTime).getTime() !== startTime.getTime() ||
		new Date(existingSession.endTime).getTime() !== endTime.getTime()
	);
}

export function isLiveSessionCapacityChanged(existingSession: LiveSession, { capacity }: PendingLiveSession) {
	return existingSession.capacity !== capacity;
}

export function isLiveSessionProctorGuideChanged(existingSession: LiveSession, { proctorGuide }: PendingLiveSession) {
	return existingSession.proctorGuide != proctorGuide;
}

function isNullableTimeStringEqual(existingTime?: string | null, newTime?: Date) {
	const existingTimestamp = existingTime ? new Date(existingTime).getTime() : undefined;
	return existingTimestamp === newTime?.getTime();
}

export function areLiveSessionRegistrationTimesChanged(
	existingSession: LiveSession,
	{ registrationStartTime, registrationEndTime }: PendingLiveSession,
) {
	return (
		!isNullableTimeStringEqual(existingSession.registrationStartTime, registrationStartTime) ||
		!isNullableTimeStringEqual(existingSession.registrationEndTime, registrationEndTime)
	);
}

export function isLiveSessionEntityChanged(existingSession: LiveSession, pendingSession: PendingLiveSession) {
	return (
		isLiveSessionNameChanged(existingSession, pendingSession) ||
		areLiveSessionTimesChanged(existingSession, pendingSession) ||
		isLiveSessionCapacityChanged(existingSession, pendingSession) ||
		areLiveSessionRegistrationTimesChanged(existingSession, pendingSession) ||
		isLiveSessionProctorGuideChanged(existingSession, pendingSession)
	);
}

export function isLiveSessionProctorChanged(
	existingSession: LiveSessionWithInvitations,
	{ proctorId }: PendingLiveSession,
) {
	return (
		(proctorId && !existingSession.users.some(({ userId, role }) => role === "proctor" && userId === proctorId)) ||
		(!proctorId && existingSession.users.length > 0 && existingSession.invitations.length === 0)
	);
}

export function isLiveSessionProctorGroupChanged(
	existingSession: LiveSessionWithInvitations,
	{ proctorGroupId }: PendingLiveSession,
) {
	return (
		(proctorGroupId &&
			!existingSession.invitations.some((proctorGroup) => proctorGroup.proctorGroupId === proctorGroupId)) ||
		(!proctorGroupId && existingSession.invitations.length > 0)
	);
}

export function isLiveSessionDemoMode({ name }: PendingLiveSession) {
	return name.trim().endsWith("(Demo)");
}

export function checkProctorGroupNotice({ startTime }: PendingLiveSession, { minHoursNotice }: ProctorGroupInfo) {
	const currTimestamp = Date.now();
	return !minHoursNotice || currTimestamp < startTime.getTime() - minHoursNotice * 60 * 60 * 1000;
}

export function checkProctorGroupCancellationTime(
	{ startTime, registrationEndTime }: PendingLiveSession,
	{ autoCancelEmptyHoursBefore }: ProctorGroupInfo,
) {
	return (
		autoCancelEmptyHoursBefore == null ||
		(registrationEndTime &&
			registrationEndTime.getTime() <= startTime.getTime() - autoCancelEmptyHoursBefore * 60 * 60 * 1000)
	);
}

export function getPotentiallyCancelledInvitations(
	existingSession: LiveSessionWithInvitations,
	proctorGroups: Record<number, ProctorGroupInfo>,
) {
	const currTimestamp = Date.now();
	return existingSession.invitations.filter(({ proctorGroupId }) => {
		const proctorGroup = proctorGroups[proctorGroupId];
		return (
			proctorGroup?.trackCancellationHoursBefore &&
			proctorGroup?.trackCancellationMessage &&
			currTimestamp >=
				new Date(existingSession.startTime).getTime() - proctorGroup.trackCancellationHoursBefore * 60 * 60 * 1000
		);
	});
}

export function getLiveSessionsCancelledByUpdate(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
	proctorGroups: Record<number, ProctorGroupInfo>,
) {
	return Object.values(pendingSessions).filter((pendingSession) => {
		const existingSession = pendingSession.id ? existingSessions[pendingSession.id] : undefined;
		return (
			existingSession &&
			isLiveSessionProctorGroupChanged(existingSession, pendingSession) &&
			getPotentiallyCancelledInvitations(existingSession, proctorGroups).length > 0
		);
	});
}

export function getLiveSessionErrors(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
	proctorGroups: Record<number, ProctorGroupInfo>,
): Record<string, SessionIssue[]> {
	const currTime = new Date();
	if (Object.keys(pendingSessions).length === 0) {
		return { _: ["noSessions"] };
	}
	return Object.fromEntries(
		Object.values(pendingSessions).map((pendingSession) => {
			const {
				identifier,
				name,
				startTime,
				endTime,
				capacity,
				proctorId,
				proctorGroupId,
				registrationStartTime,
				registrationEndTime,
			} = pendingSession;
			const existingSession = existingSessions[identifier];
			const nameChanged = !existingSession || isLiveSessionNameChanged(existingSession, pendingSession);
			const timesChanged = !existingSession || areLiveSessionTimesChanged(existingSession, pendingSession);
			const capacityChanged = !existingSession || isLiveSessionCapacityChanged(existingSession, pendingSession);
			const registrationTimesChanged =
				!existingSession || areLiveSessionRegistrationTimesChanged(existingSession, pendingSession);

			const proctorGroupChanged = !existingSession || isLiveSessionProctorGroupChanged(existingSession, pendingSession);
			const isCancelled =
				existingSession &&
				getPotentiallyCancelledInvitations(existingSession, proctorGroups).length > 0 &&
				proctorGroupChanged;
			const proctorChanged =
				!existingSession || isLiveSessionProctorChanged(existingSession, pendingSession) || proctorGroupChanged;

			const issues: SessionIssue[] = [];
			if (nameChanged && !name) {
				issues.push("noName");
			}
			if (timesChanged) {
				if (startTime >= endTime) {
					issues.push("timesInvalid");
				}
				if (startTime <= currTime) {
					issues.push("startTimePassed");
				}
				if (endTime <= currTime) {
					issues.push("endTimePassed");
				}
				if (!isLiveSessionDemoMode(pendingSession) && endTime.getTime() - startTime.getTime() > 24 * 60 * 60 * 1000) {
					issues.push("durationTooLong");
				}
			}
			if (registrationTimesChanged) {
				if (registrationStartTime && registrationEndTime && registrationStartTime >= registrationEndTime) {
					issues.push("registrationTimesInvalid");
				}
				if (registrationStartTime && registrationStartTime <= currTime) {
					issues.push("registrationStartTimePassed");
				}
				if (registrationEndTime && registrationEndTime <= currTime) {
					issues.push("registrationEndTimePassed");
				}
				if (!registrationStartTime !== !registrationEndTime) {
					issues.push("unfilledRegistrationTimeField");
				}
			}
			if (timesChanged || registrationTimesChanged) {
				if (registrationStartTime && currTime < startTime && registrationStartTime > startTime) {
					issues.push("registrationStartTimeAfterStartTime");
				}
				if (registrationEndTime && registrationEndTime > endTime) {
					issues.push("registrationEndTimeAfterEndTime");
				}
			}
			if (capacityChanged && capacity <= 0) {
				issues.push("capacityInvalid");
			}
			if (proctorChanged && !proctorId && !proctorGroupId) {
				issues.push("noProctor");
			} else if ((proctorChanged || isCancelled) && proctorGroupId) {
				const proctorGroup = proctorGroups[proctorGroupId];
				if (proctorGroup && !checkProctorGroupNotice(pendingSession, proctorGroup)) {
					issues.push("proctorGroupNotice");
				}
			}
			if ((timesChanged || registrationTimesChanged || proctorGroupChanged) && proctorGroupId) {
				const proctorGroup = proctorGroups[proctorGroupId];
				if (proctorGroup && !checkProctorGroupCancellationTime(pendingSession, proctorGroup)) {
					issues.push("registrationEndTimeAfterCancellationTime");
				}
			}
			return [identifier, issues] satisfies [string, SessionIssue[]];
		}),
	);
}

export function spansMultipleDays(startTime: Date, endTime: Date) {
	return changeTimeOnly(startTime, 0, 0).getTime() !== changeTimeOnly(endTime, 0, 0).getTime();
}

export function resetStartedLiveSessions(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
): Record<string, PendingLiveSession> {
	const currTime = new Date();
	return Object.fromEntries(
		Object.entries(pendingSessions)
			.map(([identifier, session]): [string, PendingLiveSession] => {
				const existingSession = session.id ? existingSessions[session.id] : undefined;
				if (existingSession && currTime >= new Date(existingSession.startTime)) {
					const { name, startTime, endTime, capacity, proctorGroupId } =
						convertLiveSessionToPendingSession(existingSession);
					return [
						identifier,
						{
							...session,
							name,
							startTime,
							endTime,
							capacity,
							proctorId: proctorGroupId ? undefined : session.proctorId,
							proctorGroupId,
						},
					];
				}
				return [identifier, session];
			})
			// restore deleted sessions
			.concat(
				Object.values(existingSessions)
					.filter(({ id, startTime }) => !(id in pendingSessions) && currTime >= new Date(startTime))
					.map((session) => [session.id, convertLiveSessionToPendingSession(session)]),
			),
	);
}

function getCreatedLiveSessions(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
) {
	return Object.values(pendingSessions).filter(({ identifier }) => !(identifier in existingSessions));
}

function getDeletedLiveSessions(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
) {
	return Object.values(existingSessions).filter(({ id }) => !(id in pendingSessions));
}

function getLiveSessionsWithExisting(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
) {
	return Object.values(pendingSessions)
		.map(
			(session) =>
				[session, session.id ? existingSessions[session.id] : undefined] satisfies [
					PendingLiveSession,
					LiveSessionWithInvitations | undefined,
				],
		)
		.filter(
			(
				sessions,
			): sessions is [
				PendingLiveSession & {
					id: string;
				},
				LiveSessionWithInvitations,
			] => !!sessions[1],
		);
}

function getUpdatedLiveSessions(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
) {
	return getLiveSessionsWithExisting(existingSessions, pendingSessions).filter(([pendingSession, existingSession]) =>
		isLiveSessionEntityChanged(existingSession, pendingSession),
	);
}

type ProctorIds = { liveSessionId: string; userId: string };

function getProctorModifications(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
): [ProctorIds[], ProctorIds[]] {
	const addedProctors: ProctorIds[] = [];
	let removedProctors: ProctorIds[] = [];
	const updatedSessions = getLiveSessionsWithExisting(existingSessions, pendingSessions);
	updatedSessions.forEach(([pendingSession, existingSession]) => {
		if (isLiveSessionProctorChanged(existingSession, pendingSession)) {
			removedProctors = removedProctors.concat(existingSession.users.filter(({ role }) => role === "proctor"));
			if (pendingSession.proctorId) {
				addedProctors.push({ liveSessionId: pendingSession.id, userId: pendingSession.proctorId });
			}
		}
	});
	return [addedProctors, removedProctors];
}

type InvitationIds = { liveSessionId: string; proctorGroupId: number };

function getAddedInvitations(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
) {
	const addedInvitations: InvitationIds[] = [];
	getLiveSessionsWithExisting(existingSessions, pendingSessions).forEach(([pendingSession, existingSession]) => {
		if (isLiveSessionProctorGroupChanged(existingSession, pendingSession)) {
			if (pendingSession.proctorGroupId) {
				addedInvitations.push({
					liveSessionId: pendingSession.id,
					proctorGroupId: pendingSession.proctorGroupId,
				});
			}
		}
	});
	getCreatedLiveSessions(existingSessions, pendingSessions).forEach(({ id, proctorGroupId }) => {
		if (id && proctorGroupId) {
			addedInvitations.push({ liveSessionId: id, proctorGroupId });
		}
	});
	return addedInvitations;
}

function getRemovedInvitations(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
) {
	let removedInvitations: InvitationIds[] = [];
	getLiveSessionsWithExisting(existingSessions, pendingSessions).forEach(([pendingSession, existingSession]) => {
		if (isLiveSessionProctorGroupChanged(existingSession, pendingSession)) {
			removedInvitations = removedInvitations.concat(existingSession.invitations);
		}
	});
	return removedInvitations;
}

type SaveLiveSessionErrorDetails = { sessionId: string; error: unknown };

function getErrorMessage(
	error: unknown,
	existingSession?: LiveSessionWithInvitations,
	pendingSession?: PendingLiveSession,
) {
	const sessionName = (pendingSession ?? existingSession)?.name;
	let message = `The settings for ${sessionName} could not be applied. Please try again.`;
	if (error instanceof ApiClientResponseError) {
		if (error.status === 413) {
			message = `The Proctor Guide section for ${sessionName} exceeds the maximum size. Please try to reduce the size of images if applicable.`;
		} else {
			switch (error.code) {
				case "insufficientNotice": {
					const minHoursNotice = (
						error.details as
							| {
									proctorGroupName?: string;
									minHoursNotice?: number;
							  }
							| undefined
					)?.minHoursNotice;
					if (minHoursNotice) {
						message = `The proctoring group for ${sessionName} cannot be applied. The group must be notified ${minHoursNotice} hour${minHoursNotice === 1 ? "" : "s"} before the start time.`;
					} else {
						message = `The proctoring group for ${sessionName} does not have enough time to be notified.`;
					}
					break;
				}
				case "timesPast":
					message = `The Start Time for ${sessionName} has already passed. Please change it to a future time.`;
					break;
				case "alreadyStarted":
				case "alreadyEnded":
					message = `${sessionName} cannot be edited as it is currently in progress or has finished.`;
					break;
				case "registrationTimesPast":
					message = `“${sessionName}” requires you to choose a registration date or time that has not already passed.`;
					break;
				default:
					if (error.originalMessage) {
						message = `${sessionName}: ${error.originalMessage}`;
					}
					break;
			}
		}
	}
	return message;
}

function getErrorMessages(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
	errors: SaveLiveSessionErrorDetails[],
) {
	return errors.map(({ sessionId, error }) => {
		return getErrorMessage(error, existingSessions[sessionId], pendingSessions[sessionId]);
	});
}

export class LiveSessionError extends Error {
	readonly errors: SaveLiveSessionErrorDetails[];
	readonly messages: string[];

	constructor(
		existingSessions: Record<string, LiveSessionWithInvitations>,
		pendingSessions: Record<string, PendingLiveSession>,
		errors: SaveLiveSessionErrorDetails[],
	) {
		const messages = getErrorMessages(existingSessions, pendingSessions, errors);
		super(messages.join(", "));
		this.errors = errors;
		this.messages = messages;
	}
}

export async function saveLiveSessions({
	api,
	assessmentUuids,
	existingSessions,
	pendingSessions,
}: {
	api: VerityApi;
	assessmentUuids: AssessmentUuids;
	existingSessions: Record<string, LiveSessionWithInvitations>;
	pendingSessions: Record<string, PendingLiveSession>;
}) {
	const errors: SaveLiveSessionErrorDetails[] = [];

	const newLiveSessions: Record<string, PendingLiveSession & { id: string }> = {};
	const removedInvitations = getRemovedInvitations(existingSessions, pendingSessions);
	await Promise.all(
		removedInvitations.map(({ liveSessionId, proctorGroupId }) =>
			api.deleteProctorGroupInvitation(liveSessionId, proctorGroupId).catch((error) =>
				errors.push({
					sessionId: liveSessionId,
					error,
				}),
			),
		),
	);

	await Promise.all(
		getCreatedLiveSessions(existingSessions, pendingSessions).map((session) =>
			api
				.createLiveSession(assessmentUuids, {
					...session,
					registrationStartTime: session.registrationStartTime ?? null,
					registrationEndTime: session.registrationEndTime ?? null,
					users: session.proctorId ? [{ userId: session.proctorId, role: "proctor" }] : undefined,
				})
				.then(({ id }) => {
					newLiveSessions[session.identifier] = { ...session, id };
				})
				.catch((error) => {
					errors.push({ sessionId: session.identifier, error });
				}),
		),
	);
	await Promise.all(
		getDeletedLiveSessions(existingSessions, pendingSessions).map(({ id }) =>
			api.deleteLiveSession(id).catch((error) => errors.push({ sessionId: id, error })),
		),
	);
	await Promise.all(
		getUpdatedLiveSessions(existingSessions, pendingSessions).map(([session]) =>
			api
				.updateLiveSession(session.id, {
					...session,
					registrationStartTime: session.registrationStartTime ?? null,
					registrationEndTime: session.registrationEndTime ?? null,
				})
				.catch((error) => {
					errors.push({
						sessionId: session.id,
						error,
					});
				}),
		),
	);

	if (errors.length > 0) {
		throw new LiveSessionError(existingSessions, pendingSessions, errors);
	}

	const [addedProctors, removedProctors] = getProctorModifications(existingSessions, pendingSessions);
	await Promise.all(
		addedProctors.map(({ liveSessionId, userId }) =>
			api.addLiveSessionProctor(liveSessionId, userId).catch((error) =>
				errors.push({
					sessionId: liveSessionId,
					error,
				}),
			),
		),
	);
	await Promise.all(
		removedProctors.map(({ liveSessionId, userId }) =>
			api.removeLiveSessionProctor(liveSessionId, userId).catch((error) =>
				errors.push({
					sessionId: liveSessionId,
					error,
				}),
			),
		),
	);

	const addedInvitations = getAddedInvitations(existingSessions, {
		...pendingSessions,
		...newLiveSessions,
	});
	await Promise.all(
		addedInvitations.map(({ liveSessionId, proctorGroupId }) =>
			api.createProctorGroupInvitation(liveSessionId, proctorGroupId).catch((error) =>
				errors.push({
					sessionId: liveSessionId,
					error,
				}),
			),
		),
	);

	if (errors.length > 0) {
		throw new LiveSessionError(existingSessions, pendingSessions, errors);
	}
}
